<template>
  <div>
    <div class="row">
      <div class="col-12">
        <Card body-classes="p-0" class="mb-5 shadow-sm">
          <template v-slot:header>
            <div class="d-flex align-items-center justify-content-between">
              <h4 class="m-0 text-primary">
                <i class="fas fa-history me-2"></i>System Logs
                <span class="badge bg-primary ms-2">{{logs.data.length}}</span>
              </h4>
              <button class="btn btn-outline-primary btn-sm" @click="resetFilter">
                <i class="fas fa-sync-alt me-1"></i> Reset Filters
              </button>
            </div>
          </template>

          <!-- Advanced Filter Section -->
          <div class="col-md-12 p-3 bg-light border-bottom">
            <div class="row g-3">
              <div class="col-md-6 col-lg-4 col-xl-2">
                <label class="form-label fw-bold text-muted small">User</label>
                <select class="form-select form-select-sm" v-model="search.causer_id">
                  <option value="" selected>All users</option>
                  <option v-for="user in users" :key="user.id" :value="user.id">{{user.first_name}} {{user.last_name}}</option>
                </select>
              </div>
              <div class="col-md-6 col-lg-4 col-xl-2">
                <label class="form-label fw-bold text-muted small">Module</label>
                <select class="form-select form-select-sm" v-model="search.log_name">
                  <option value="" selected>All modules</option>
                  <option v-for="module in modules" :key="module.id" :value="module.module">{{module.name}}</option>
                </select>
              </div>
              <div class="col-md-6 col-lg-4 col-xl-2">
                <label class="form-label fw-bold text-muted small">Action</label>
                <select class="form-select form-select-sm" v-model="search.event">
                  <option value="" selected>All actions</option>
                  <option value="created">Created</option>
                  <option value="updated">Updated</option>
                  <option value="deleted">Deleted</option>
                  <option value="login">Login</option>
                  <option value="logout">Logout</option>
                </select>
              </div>
              <div class="col-md-6 col-lg-4 col-xl-2">
                <label class="form-label fw-bold text-muted small">From Date</label>
                <input type="date" class="form-control form-control-sm" v-model="search.from">
              </div>
              <div class="col-md-6 col-lg-4 col-xl-2">
                <label class="form-label fw-bold text-muted small">To Date</label>
                <input type="date" class="form-control form-control-sm" v-model="search.to">
              </div>
              <div class="col-md-6 col-lg-4 col-xl-2">
                <label class="form-label fw-bold text-muted small">Per Page</label>
                <select class="form-select form-select-sm" v-model="search.per_page">
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Table Section -->
          <div class="col-md-12 table-responsive">
            <table class="table table-hover table-striped">
              <thead class="table-light">
              <tr>
                <th scope="col">User</th>
                <th scope="col">Module</th>
                <th scope="col">Action</th>
                <th scope="col">Description</th>
                <th scope="col">Date</th>
                <th class="text-center">Details</th>
              </tr>
              </thead>
              <tbody v-if="!loading">
              <tr v-for="log in logs.data" :key="log.id">
                <td>{{ log.description }}</td>
                <td>
                  <span class="badge bg-secondary">{{ log.log_name }}</span>
                </td>
                <td>
                  <span :class="getActionBadgeClass(log.event)">{{ log.event }}</span>
                </td>
                <td>{{ log.event }} {{ log.log_name }}</td>
                <td>
                  <i class="far fa-clock me-1"></i>
                  {{ formatDate(log.created_at) }}
                </td>
                <td class="text-center">
                  <button
                      class="btn btn-outline-primary btn-sm"
                      @click="logModal = true; populateLog(log)"
                  >
                    <i class="fas fa-eye me-1"></i> View
                  </button>
                </td>
              </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="6" class="text-center py-4">
                    <Spinner spinner-class="mb-1" />
                  </td>
                </tr>
              </tbody>
              <tbody v-if="!loading && logs.data.length === 0">
                <tr>
                  <td colspan="6" class="text-center py-4 text-muted">
                    <i class="fas fa-info-circle me-2"></i>No logs found matching your criteria
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination Section -->
          <div class="col-md-12 d-flex justify-content-between align-items-center p-3 bg-light border-top">
            <div>
              <button class="btn btn-outline-secondary btn-sm" @click="$router.go(-1)">
                <i class="fas fa-arrow-left me-1"></i> Back
              </button>
            </div>
            <div>
              <Pagination :pages="logs.last_page" v-model="search.page" class="mb-0">
                <span slot="next">Next</span>
                <span slot="previous">Prev</span>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <!-- Show Log Modal -->
    <Modal :open="logModal" dialog-size="modal-lg" @closed="logModal = false">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="fas fa-info-circle me-2 text-primary"></i>Log Details
        </h5>
        <button @click="logModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div class="card">
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <div class="d-flex">
                  <div class="text-muted me-2">
                    <i class="fas fa-user text-primary"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 text-muted">User</h6>
                    <p class="mb-0">{{ log.description }}</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="d-flex">
                  <div class="text-muted me-2">
                    <i class="fas fa-tag text-primary"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 text-muted">Action</h6>
                    <p class="mb-0">
                      <span :class="getActionBadgeClass(log.event)">{{ log.event }}</span>
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="d-flex">
                  <div class="text-muted me-2">
                    <i class="fas fa-cube text-primary"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 text-muted">Module</h6>
                    <p class="mb-0">
                      <span class="badge bg-secondary">{{ log.log_name }}</span>
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="d-flex">
                  <div class="text-muted me-2">
                    <i class="far fa-clock text-primary"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 text-muted">Date & Time</h6>
                    <p class="mb-0">{{ formatDate(log.created_at) }}</p>
                  </div>
                </div>
              </div>
              <div class="col-12">
                <div class="d-flex">
                  <div class="text-muted me-2">
                    <i class="fas fa-align-left text-primary"></i>
                  </div>
                  <div>
                    <h6 class="mb-1 text-muted">Description</h6>
                    <p class="mb-0">{{ log.event + ' ' + log.log_name }}</p>
                  </div>
                </div>
              </div>
              <div class="col-12 mt-4">
                <h6 class="border-bottom pb-2 mb-3">
                  <i class="fas fa-database text-primary me-2"></i>Data Details
                </h6>
                <pre class="bg-light p-3 rounded">{{ log.properties }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="logModal = false">
          <i class="fas fa-times me-1"></i> Close
        </button>
      </div>
    </Modal>
    <!-- Show Log Modal End -->

  </div>
</template>

<script>

import settingService from "@/api/settingService";
import userService from "@/api/userService";
import {encrypt, decrypt} from "@/utils/crypto";

export default {
  name: "Logs",

  data() {
    return {
      loading: true,
      logs: {
        data: []
      },
      log: {},
      users: [],
      modules: [],
      logModal: false,
      search: {
        page: 1,
        per_page: 10,
        log_name: '',
        causer_id: '',
        event: '',
        from: '',
        to: '',
      },
    }
  },

  methods: {
    populateLog(log) {
      this.log = log
    },
    async resetFilter() {
      this.search = {
        page: 1,
        per_page: 10,
        log_name: '',
        causer_id: '',
        event: '',
        from: '',
        to: '',
      }
      await this.getLogs()
    },
    async getLogs() {
      this.loading = true
      this.logs = await settingService.getLogs(this.search)
      this.loading = false
    },
    async getUsersAndModules() {
      // Clear cache to force fresh data load
      sessionStorage.removeItem('users')
      sessionStorage.removeItem('modules')

      const usersData = await userService.getUsers({ per_page: 1000 })
      if (Array.isArray(usersData?.data)) {
        this.users = usersData.data.filter(u => u != null)
      } else if (Array.isArray(usersData)) {
        this.users = usersData.filter(u => u != null)
      } else {
        this.users = []
      }
      this.modules = await settingService.getModules()
      sessionStorage.setItem('users', encrypt(JSON.stringify(this.users)))
      sessionStorage.setItem('modules', encrypt(JSON.stringify(this.modules)))
    },
    getActionBadgeClass(action) {
      const classes = {
        created: 'badge bg-success',
        updated: 'badge bg-primary',
        deleted: 'badge bg-danger',
        login: 'badge bg-info',
        logout: 'badge bg-warning text-dark'
      }
      return classes[action] || 'badge bg-secondary'
    },
    formatDate(dateString) {
      if (!dateString) return ''
      return dateString.replace("T", ' ').replace(".000000Z", '')
    }
  },

  async created() {
    await this.getLogs()
    await this.getUsersAndModules()
  },

  watch: {
    search: {
      async handler(val) {
        await this.getLogs()
      },
      deep: true
    }
  }
}
</script>

<style scoped>

</style>
